import type { RefetchOptions } from "@tanstack/react-query";
import { isFunction } from "lodash";
import React, { ReactNode, useContext, useMemo } from "react";

import { useUserQuery } from "@sol/sdk/users/queries/userQuery";
import { useUserSettingsQuery } from "@sol/sdk/users/queries/userSettingsQuery";
import type { IUser } from "@sol/sdk/users/types/IUser";
import type { IUserSettings } from "@sol/sdk/users/types/IUserSettings";

export type ContextType = {
    user?: IUser;
    settings?: IUserSettings;
    isLoading: boolean;
    refetchUser: (options?: RefetchOptions | undefined) => Promise<IUser | undefined>;
    refetchSettings: (options?: RefetchOptions | undefined) => Promise<IUserSettings | undefined>;
};

const Context = React.createContext<ContextType>({} as ContextType);

const { Provider } = Context;

type ProviderRenderProp = (context: ContextType) => ReactNode;

type Props = {
    userId?: string | null;
    children: ReactNode | ProviderRenderProp;
};

export const AuthenticatedUserProvider = ({ children, userId }: Props) => {
    const {
        data: user,
        isLoading: isUserLoading,
        refetch: refetchUser,
    } = useUserQuery(
        {
            user: userId ?? undefined,
        },
        {
            suspense: true,
            enabled: !!userId,
        },
    );

    const {
        data: settings,
        refetch: refetchSettings,
        isLoading: areSettingsLoading,
    } = useUserSettingsQuery(
        {
            user: userId!,
        },
        {
            suspense: true,
            enabled: !!userId,
        },
    );

    const value = useMemo(() => {
        return {
            user,
            settings,
            refetchUser: async (options?: RefetchOptions) => {
                const result = await refetchUser(options);
                return result.data;
            },
            refetchSettings: async (options?: RefetchOptions) => {
                const result = await refetchSettings(options);
                return result.data;
            },
            isLoading: isUserLoading || areSettingsLoading || !user || !settings,
        };
    }, [user, settings, isUserLoading, areSettingsLoading, refetchUser, refetchSettings]);

    return <Provider value={value}>{children && isFunction(children) ? children(value) : children}</Provider>;
};

export const useAuthenticatedUser = () => {
    const context = useContext(Context);
    const { user, settings } = context;

    if (!user || !settings) {
        throw new Error("No user and or settings found. Use this inside AuthenticatedLayout");
    }

    return { ...context, user, settings };
};

export default Context;
